export class NCALayerClient {
	websocket: WebSocket | null
	openCallbacks: ((eventData: any) => void)[] = []
	errorCallbacks: ((eventData: any) => void)[] = []
	closeCallbacks: ((eventData: any) => void)[] = []
	msgCallbacks: ((eventData: string) => void)[] = []
	private connectionTimeout: number = 30000 // 5 minutes timeout (300 seconds)
	private requestTimeout: number = 30000 // 5 minutes for individual requests

	constructor() {
		this.websocket = null
	}

	open(): Promise<WebSocket> {
		return new Promise((resolve, reject) => {
			if (this.websocket) {
				resolve(this.websocket)
				return
			}

			// Set connection timeout
			const timeoutId = setTimeout(() => {
				// Don't close connection on timeout, just reject
				reject(new Error('Connection timeout'))
			}, this.connectionTimeout)

			this.websocket = new WebSocket('wss://127.0.0.1:13579/')

			this.websocket.onopen = (response: any) => {
				clearTimeout(timeoutId)
				this.openCallbacks.forEach((callback) => {
					callback(response)
				})
				if (this.websocket) resolve(this.websocket)
			}

			this.websocket.onerror = (error) => {
				clearTimeout(timeoutId)
				this.errorCallbacks.forEach((callback) => {
					callback(error)
				})
				this.close()
				reject(error)
			}

			this.websocket.onclose = (closeEvent) => {
				clearTimeout(timeoutId)
				this.closeCallbacks.forEach((callback) => {
					callback(closeEvent)
				})
				if (closeEvent.wasClean) {
					this.websocket = null
				} else {
					// Connection was not clean, likely an error
					this.websocket = null
				}
			}

			this.websocket.onmessage = async (message) => {
				try {
					const data = JSON.parse(message.data)
					if (data.code == 500) {
						this.close()
						// Reject with specific error for certificate issues
						reject(new Error('Certificate error: ' + (data.message || 'Unknown certificate error')))
					} else if (data.responseObject) {
						this.msgCallbacks.forEach((callback) => {
							callback(data.responseObject)
						})
					} else if (data.error) {
						// Handle NCALayer specific errors
						this.close()
						reject(new Error('NCALayer error: ' + data.error))
					}
				} catch (parseError) {
					this.close()
					reject(new Error('Invalid response from NCALayer'))
				}
			}
		})
	}

	async request(request: any): Promise<string> {
		this.websocket = await this.open()
		return new Promise((resolve, reject) => {
			if (!this.websocket) {
				reject(new Error('WebSocket connection failed'))
				return
			}

			// Set request timeout
			const requestTimeout = setTimeout(() => {
				// Don't close connection on timeout, just reject
				reject(new Error('Request timeout'))
			}, this.requestTimeout)

			this.msgCallbacks.push((msg: any) => {
				clearTimeout(requestTimeout)
				resolve(msg)
			})

			this.errorCallbacks.push((error: any) => {
				clearTimeout(requestTimeout)
				reject(error)
			})

			this.closeCallbacks.push((error: any) => {
				clearTimeout(requestTimeout)
				reject(error)
			})

			try {
				this.websocket.send(JSON.stringify(request))
			} catch (sendError) {
				clearTimeout(requestTimeout)
				reject(new Error('Failed to send request to NCALayer'))
			}
		})
	}

	async send(): Promise<string> {
		const request = {
			module: 'kz.gov.pki.knca.commonUtils',
			method: 'signXml',
			args: [
				'PKCS12',
				'AUTHENTICATION',
				'<login><sessionid>caacda70-fd36-45ed-8d94-45a88890f83a</sessionid></login>',
				'',
				'',
			],
		}

		return await this.request(request)
	}

	async signDoc(data: string): Promise<string> {
		const request = {
			module: 'kz.gov.pki.knca.commonUtils',
			method: 'createCAdESFromBase64',
			args: ['PKCS12', 'SIGNATURE', data, true],
		}

		return await this.request(request)
	}

	close() {
		if (this.websocket) {
			this.websocket.close()
			this.websocket = null
		}
	}

	static async normalizeDataToSign(data: any) {
		const normalizeDataBlock = async (dataBlock: any) => {
			if (typeof dataBlock === 'string') {
				return dataBlock
			}

			let dataBlockArrayBuffer = dataBlock
			if (dataBlock instanceof Blob) {
				dataBlockArrayBuffer = await dataBlock.arrayBuffer()
			}

			return NCALayerClient.arrayBufferToB64(dataBlockArrayBuffer)
		}

		if (Array.isArray(data)) {
			return Promise.all(data.map(normalizeDataBlock))
		}

		return normalizeDataBlock(data)
	}

	static arrayBufferToB64(arrayBuffer: any) {
		const bytes = new Uint8Array(arrayBuffer)
		let binary = ''
		const len = bytes.byteLength

		for (let i = 0; i < len; i += 1) {
			binary += String.fromCharCode(bytes[i])
		}

		return window.btoa(binary)
	}
}

export const NCALayerClientIns = new NCALayerClient()
